<template>
  <layout-basic>
    <page-hero
      title="Grow Your Business with IMPROVE"
      lead="Help your customers while creating recurring revenue."
    />

    <section class="partner-benefits">
      <v-container>
        <h2 class="font-sora partner-benefits__title">
          Partnerships Built for Long-Term Growth
        </h2>
        <p class="partner-benefits__lead">
          Help your customers succeed while building recurring revenue with a
          learning platform designed for growing businesses.
        </p>
      </v-container>
      <point-grid :points="benefits" :columns="2" />
    </section>

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="about" />
      </v-container>
    </section>

    <section class="partner-types">
      <v-container>
        <h2 class="font-sora partner-types__title">Partner types</h2>
        <div class="partner-types__grid">
          <div
            v-for="type in partnerTypes"
            :key="type"
            class="partner-types__card"
          >
            {{ type }}
          </div>
        </div>
      </v-container>
    </section>

    <section class="partner-commitment">
      <v-container>
        <div class="partner-commitment__card">
          <h2 class="font-sora partner-commitment__title">
            A Personal Commitment from Me
          </h2>
          <blockquote class="partner-commitment__quote">
            “When you become an IMPROVE Partner, you’re not joining a large,
            anonymous software company. You’re working with a team that
            genuinely values long-term relationships. My commitment is simple:
            we’ll support you, listen to your feedback, and work with you to
            help your customers succeed. If our partners grow, IMPROVE grows
            too.”
          </blockquote>
          <p class="partner-commitment__sign">— Steve Walker, Founder</p>
        </div>
      </v-container>
    </section>

    <section class="partner-dual-cta">
      <v-container>
        <h2 class="font-sora partner-dual-cta__title">Let’s grow together.</h2>
        <div class="partner-dual-cta__actions">
          <a href="/contact" class="partner-dual-cta__btn partner-dual-cta__btn--primary">
            Become a Partner
          </a>
          <a href="/contact" class="partner-dual-cta__btn partner-dual-cta__btn--secondary">
            Talk to Us
          </a>
        </div>
      </v-container>
    </section>

    <standard-cta
      title="Ready to take the next step?"
      lead="See why growing businesses are choosing IMPROVE to onboard, train and develop their people."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { marketingPageMeta } from '@/utils/marketingPage';

export default {
  name: 'PartnerProgramme',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    StandardCta,
    DemoVideo,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'Grow Your Business with IMPROVE | Partners',
      description:
        'Help your customers succeed while building recurring revenue. Join the IMPROVE Partner Programme.',
      path: '/partner-programme',
    });
  },
  data() {
    return {
      benefits: [
        'Recurring revenue opportunities',
        'Dedicated partner support',
        'Sales & marketing resources',
        'Customer Success team',
        'Product training',
        'Joint growth planning',
      ],
      partnerTypes: [
        'HR Consultants',
        'Training Providers',
        'Software Partners',
        'Business Advisers',
        'Membership Organisations',
      ],
    };
  },
};
</script>

<style scoped>
.partner-benefits {
  padding: 8px 0 16px;
}

.partner-benefits__title,
.partner-types__title,
.partner-dual-cta__title {
  text-align: center;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px;
}

.partner-benefits__lead {
  text-align: center;
  max-width: 640px;
  margin: 0 auto 8px;
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  line-height: 1.5;
}

.partner-types {
  padding: 40px 0;
  background: #f7f9fb;
}

.partner-types__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 900px;
  margin: 0 auto;
}

.partner-types__card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 20px 16px;
  text-align: center;
  color: var(--brand-navy, #002458);
  font-weight: 700;
  font-size: 16px;
}

.partner-commitment {
  padding: 48px 0;
}

.partner-commitment__card {
  max-width: 720px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 32px 28px;
  box-shadow: 0 8px 24px rgba(0, 36, 88, 0.06);
}

.partner-commitment__title {
  color: var(--brand-navy, #002458);
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 16px;
  text-align: center;
}

.partner-commitment__quote {
  margin: 0;
  color: var(--brand-navy, #002458);
  font-size: 17px;
  line-height: 1.65;
  font-style: italic;
}

.partner-commitment__sign {
  margin: 18px 0 0;
  text-align: right;
  color: var(--brand-teal, #08a0a8);
  font-weight: 700;
}

.partner-dual-cta {
  padding: 16px 0 40px;
  text-align: center;
}

.partner-dual-cta__actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.partner-dual-cta__btn {
  display: inline-block;
  padding: 14px 28px;
  border-radius: 6px;
  font-weight: 700;
  text-decoration: none;
  font-size: 16px;
}

.partner-dual-cta__btn--primary {
  background: var(--brand-navy, #002458);
  color: #fff;
}

.partner-dual-cta__btn--primary:hover {
  background: var(--brand-teal, #08a0a8);
  color: #fff;
}

.partner-dual-cta__btn--secondary {
  background: #fff;
  color: var(--brand-navy, #002458);
  border: 2px solid var(--brand-navy, #002458);
}

.partner-dual-cta__btn--secondary:hover {
  border-color: var(--brand-teal, #08a0a8);
  color: var(--brand-teal, #08a0a8);
}

@media screen and (max-width: 900px) {
  .partner-types__grid {
    grid-template-columns: 1fr;
  }
}
</style>
